'use client';

import { IcArrowDown, Typography } from '@musinsa/mds';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { PageViewTracker } from '../../common/components/PageViewTracker';
import { getCurrentTime } from '../../common/utils';
import {
  getRangeMonths,
  type MonthOptionType,
  sortRange,
  toMonthOption,
} from '../../me/sections/SalesRankingFilterSection/SalesRankingMonthSelector/utils';
import { curatorQueries } from '../../query-factory';
import { DatePeriodBottomSheet } from './DatePeriodBottomSheet';

interface DatePeriodSelectorProps {
  onPeriodChange?: (year: number, month: number) => void;
  className?: string;
}

/** registeredDate ~ now 범위의 월 목록을 ServiceMonthInfo[]로 생성 */
const buildAvailableMonths = (
  registeredDate: string | undefined,
  now: string,
): MonthOptionType[] => {
  if (!registeredDate) return [];
  const [from, to] = sortRange([registeredDate, now]);
  return getRangeMonths([from, to])
    .map(toMonthOption)
    .reverse()
    .map((m) => ({
      year: m.year,
      month: m.month,
      label: m.label ?? `${m.year}.${String(m.month).padStart(2, '0')}`,
    }));
};

export const DatePeriodSelector = ({ onPeriodChange, className }: DatePeriodSelectorProps) => {
  const { service } = useDeviceInfo();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<MonthOptionType>();

  const { data: userInfo } = useQuery({ ...curatorQueries.profile.userInfo });
  const now = getCurrentTime();
  const availableMonths = useMemo(
    () => buildAvailableMonths(userInfo?.registeredDate, now),
    [userInfo?.registeredDate, now],
  );

  const firstMonth = useMemo(
    () => (availableMonths.length > 0 ? availableMonths[0] : null),
    [availableMonths],
  );

  // 첫 번째 사용 가능한 월을 기본값으로 설정
  useEffect(() => {
    if (availableMonths.length > 0 && !selectedMonth && firstMonth) {
      setSelectedMonth(firstMonth);
      onPeriodChange?.(firstMonth.year, firstMonth.month);
    }
  }, [firstMonth, selectedMonth, onPeriodChange, availableMonths.length]);

  const handleClick = () => {
    setIsOpen(true);
  };

  const handlePeriodSelect = (month: MonthOptionType) => {
    setSelectedMonth(month);
    onPeriodChange?.(month.year, month.month);
    setIsOpen(false);
  };

  const displayText = selectedMonth?.label ?? '';

  return (
    <>
      {firstMonth && (
        <PageViewTracker
          pageName={
            service === SERVICE['29CM']
              ? 'curator_monthly_revenue'
              : 'screen_curator_monthly_revenue'
          }
          properties={{
            apply_category_name_list: firstMonth.label ?? '',
          }}
        />
      )}
      <div className={twMerge('px-4 py-3 bg-gray-100', className)}>
        <div
          className="flex items-center gap-0.5 w-fit cursor-pointer"
          onClick={handleClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleClick();
            }
          }}
          role="button"
          tabIndex={0}
        >
          <Typography size="18" weight="medium">
            {displayText}
          </Typography>
          <IcArrowDown width={20} height={20} />
        </div>
      </div>

      {isOpen && selectedMonth && (
        <DatePeriodBottomSheet
          isOpen={isOpen}
          onOpenChange={setIsOpen}
          availableMonths={availableMonths}
          selectedMonth={selectedMonth}
          onPeriodSelect={handlePeriodSelect}
        />
      )}
    </>
  );
};
